/**
 * Audition card (SPEC §5 `audition`, §6 item 8): the non-modal, docked
 * "The agent can't hear this. Can you?" card.
 *
 *   const card = mountAuditionCard(drawer.el.querySelector('[data-audition-slot]'), { onLateKeep });
 *   const outcome = await card.show({ auditionId, question, options, onPreview, onKeep, onNeither, timeoutMs });
 *
 * No backdrop and no focus trap: focus moves to the first ▶ button and is
 * restored on close. `role=region aria-live=polite` — so the eyebrow, question
 * and options are announced when the card opens; the ticking countdown is
 * `aria-hidden` while it counts so it does not spam screen readers, and the
 * "Still open" notice after a timeout is exposed again. Keys 1/2/3 keep an
 * option, Esc = neither. `show()` resolves `{ chosen, listened, reason? }`;
 * on timeout it resolves `{ chosen: null, reason: 'timeout', stillOpen: true }`
 * and LEAVES the card open so a later Keep can still land (it calls
 * `params.onKeep(index)` and then `deps.onLateKeep(index, auditionId)`).
 * `dismiss()` closes the card (Stop) and resolves `reason: 'stopped'` if pending.
 *
 * The card dispatches a bubbling `bm:audition-open` CustomEvent on `show()` so
 * the agent drawer can open itself.
 */
import './audition-card.css';
import { h, setChildren } from './dom';

export interface AuditionOption {
  label: string;
  /** One line on why the agent proposes it. */
  why?: string;
}

export interface AuditionParams {
  auditionId: string;
  question: string;
  /** 2..3 options; shown as A / B / C. */
  options: AuditionOption[];
  /** Start previewing option `index`, or the committed session (`'current'`). */
  onPreview(index: number | 'current'): void;
  /** The human kept option `index` (called before the promise resolves, and again on a late keep). */
  onKeep(index: number): void;
  /** The human wants neither (Esc / Neither button). */
  onNeither(): void;
  /** How long to wait before resolving `reason: 'timeout'`; the card stays open. */
  timeoutMs: number;
}

export interface AuditionOutcome {
  /** Index of the kept option, or null. */
  chosen: number | null;
  /** Per option: did the human press its ▶ at least once. */
  listened: boolean[];
  reason?: 'neither' | 'timeout' | 'stopped';
  /** True only on timeout: the card is still open and a later Keep will call `onLateKeep`. */
  stillOpen?: boolean;
}

export interface AuditionCardDeps {
  /** A Keep that lands after the promise already resolved on timeout. */
  onLateKeep?(index: number, auditionId: string): void;
}

export interface AuditionCardHandle {
  /** Root element (`.bm-audition`, `role=region`); hidden while closed. */
  el: HTMLElement;
  /** Open the card for one audition. A card already open is settled as `stopped` first. */
  show(params: AuditionParams): Promise<AuditionOutcome>;
  /** Close the card; resolves a pending promise with `reason: 'stopped'`. */
  dismiss(): void;
  isOpen(): boolean;
}

/** Eyebrow copy, verbatim from the spec. */
export const AUDITION_EYEBROW = "The agent can't hear this. Can you?";
/** Event name dispatched on the card (bubbling) when it opens. */
export const AUDITION_OPEN_EVENT = 'bm:audition-open';

const LETTERS = ['A', 'B', 'C', 'D'];

/** Mount the (initially hidden) audition card into `container`. */
export function mountAuditionCard(container: HTMLElement, deps: AuditionCardDeps = {}): AuditionCardHandle {
  const eyebrow = h('p', { class: 'bm-audition__eyebrow' }, AUDITION_EYEBROW);
  const question = h('h3', { class: 'bm-audition__question' });
  const list = h('ol', { class: 'bm-audition__options' });
  // Plain text: the region itself is the live region (nesting a second one would announce twice).
  const now = h('span', { class: 'bm-audition__now' }, 'Now playing: —');
  const currentBtn = h('button', { type: 'button', class: 'bm-audition__play', dataset: { play: 'current' }, 'aria-label': 'Play the current version' }, '▶ current');
  const compareRow = h('div', { class: 'bm-audition__compare' }, currentBtn, now);
  const keeps = h('div', { class: 'bm-audition__keeps' });
  const timer = h('span', { class: 'bm-audition__timer', 'aria-hidden': 'true' });
  const foot = h('div', { class: 'bm-audition__foot' }, h('span', { class: 'bm-audition__keys' }, 'Keys 1 / 2 / 3 keep · Esc neither'), timer);
  const el = h(
    'section',
    { class: 'bm-audition', role: 'region', 'aria-live': 'polite', 'aria-label': 'Audition', hidden: true },
    eyebrow,
    question,
    list,
    compareRow,
    keeps,
    foot,
  );
  container.append(el);

  interface Active {
    params: AuditionParams;
    listened: boolean[];
    resolve: (o: AuditionOutcome) => void;
    settled: boolean;
    timedOut: boolean;
    timeout?: number;
    tick?: number;
    deadline: number;
    previouslyFocused: HTMLElement | null;
  }
  let active: Active | null = null;

  const isEditable = (t: EventTarget | null) => {
    const el = t as HTMLElement | null;
    if (!el || typeof el.closest !== 'function') return false;
    return !!el.closest('input, textarea, select, [contenteditable=""], [contenteditable="true"]');
  };

  const onKey = (e: KeyboardEvent) => {
    if (!active || isEditable(e.target)) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      neither();
      return;
    }
    if (/^[1-9]$/.test(e.key)) {
      const idx = Number(e.key) - 1;
      if (idx < active.params.options.length) {
        e.preventDefault();
        e.stopPropagation();
        keep(idx);
      }
    }
  };

  function settle(outcome: AuditionOutcome): void {
    if (!active || active.settled) return;
    active.settled = true;
    active.resolve(outcome);
  }

  function close(): void {
    if (!active) return;
    const a = active;
    active = null;
    if (a.timeout) clearTimeout(a.timeout);
    if (a.tick) clearInterval(a.tick);
    document.removeEventListener('keydown', onKey, true);
    el.hidden = true;
    el.removeAttribute('data-open');
    restoreFocus(a.previouslyFocused);
  }

  /**
   * Give focus back to where it was. The grid is still `inert` at this moment
   * (the app leaves 'auditioning' only after the promise settles), so a
   * gridcell refuses focus synchronously; retry once on the next tick.
   */
  function restoreFocus(target: HTMLElement | null): void {
    if (!target || typeof target.focus !== 'function') return;
    const attempt = () => {
      if (!target.isConnected) return;
      // Never steal focus the user has since moved somewhere else.
      const cur = document.activeElement;
      if (cur && cur !== document.body && cur !== target && !el.contains(cur)) return;
      target.focus({ preventScroll: true });
    };
    attempt();
    if (document.activeElement !== target) window.setTimeout(attempt, 0);
  }

  function preview(index: number | 'current'): void {
    if (!active) return;
    if (index !== 'current') active.listened[index] = true;
    now.textContent = `Now playing: ${index === 'current' ? 'current' : `Option ${LETTERS[index]}`}`;
    for (const b of el.querySelectorAll<HTMLElement>('[data-play]')) {
      const mine = b.dataset.play === String(index);
      b.setAttribute('aria-pressed', mine ? 'true' : 'false');
    }
    active.params.onPreview(index);
  }

  function keep(index: number): void {
    if (!active) return;
    const a = active;
    a.params.onKeep(index);
    if (a.timedOut) deps.onLateKeep?.(index, a.params.auditionId);
    settle({ chosen: index, listened: a.listened.slice() });
    close();
  }

  function neither(): void {
    if (!active) return;
    const a = active;
    a.params.onNeither();
    settle({ chosen: null, listened: a.listened.slice(), reason: 'neither' });
    close();
  }

  function renderTimer(): void {
    if (!active) return;
    const text = active.timedOut
      ? 'Still open — pick when you are ready'
      : `${Math.max(0, Math.ceil((active.deadline - Date.now()) / 1000))}s`;
    // Only touch the DOM when the text changes (the region is aria-live).
    if (timer.textContent !== text) timer.textContent = text;
    timer.setAttribute('aria-hidden', active.timedOut ? 'false' : 'true');
  }

  function show(params: AuditionParams): Promise<AuditionOutcome> {
    if (active) {
      settle({ chosen: null, listened: active.listened.slice(), reason: 'stopped' });
      close();
    }
    const previouslyFocused = document.activeElement as HTMLElement | null;
    question.textContent = params.question;

    setChildren(
      list,
      params.options.map((o, i) =>
        h(
          'li',
          { class: 'bm-audition__option', dataset: { option: i } },
          h(
            'button',
            {
              type: 'button',
              class: 'bm-audition__play',
              dataset: { play: i },
              'aria-label': `Play option ${LETTERS[i]}: ${o.label}`,
              'aria-pressed': 'false',
              onClick: () => preview(i),
            },
            h('span', { class: 'bm-audition__letter', 'aria-hidden': 'true' }, `▶ ${LETTERS[i]}`),
            h('span', { class: 'bm-audition__label' }, o.label),
          ),
          o.why ? h('p', { class: 'bm-audition__why' }, o.why) : null,
        ),
      ),
    );
    setChildren(
      keeps,
      params.options.map((_o, i) =>
        h('button', { type: 'button', class: 'btn btn--primary bm-audition__keep', dataset: { keep: i }, 'aria-keyshortcuts': String(i + 1), onClick: () => keep(i) }, `Keep ${LETTERS[i]}`),
      ),
      h('button', { type: 'button', class: 'btn bm-audition__neither', dataset: { neither: '' }, 'aria-keyshortcuts': 'Escape', onClick: () => neither() }, 'Neither'),
    );
    now.textContent = 'Now playing: —';
    currentBtn.setAttribute('aria-pressed', 'false');

    return new Promise<AuditionOutcome>((resolve) => {
      const a: Active = {
        params,
        listened: params.options.map(() => false),
        resolve,
        settled: false,
        timedOut: false,
        deadline: Date.now() + Math.max(0, params.timeoutMs),
        previouslyFocused,
      };
      active = a;
      el.hidden = false;
      el.setAttribute('data-open', '');
      document.addEventListener('keydown', onKey, true);
      renderTimer();
      a.tick = window.setInterval(renderTimer, 500);
      if (params.timeoutMs > 0) {
        a.timeout = window.setTimeout(() => {
          if (active !== a || a.settled) return;
          a.timedOut = true;
          renderTimer();
          settle({ chosen: null, listened: a.listened.slice(), reason: 'timeout', stillOpen: true });
        }, params.timeoutMs);
      }
      el.dispatchEvent(new CustomEvent(AUDITION_OPEN_EVENT, { bubbles: true, detail: { auditionId: params.auditionId } }));
      el.querySelector<HTMLElement>('[data-play="0"]')?.focus();
    });
  }

  currentBtn.addEventListener('click', () => preview('current'));

  function dismiss(): void {
    if (!active) return;
    settle({ chosen: null, listened: active.listened.slice(), reason: 'stopped' });
    close();
  }

  return { el, show, dismiss, isOpen: () => active !== null };
}
